import React, { useState, useEffect } from "react";
import {
  Pencil,
  Trash,
  Eye,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import type { Patient } from "../../types";
import { useConfirm } from "../../context/ConfirmContext";
import { toast } from 'react-hot-toast';

const PatientAccount: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [patientsPerPage] = useState(10);
  const navigate = useNavigate();
  const { confirm } = useConfirm();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const fetchPatients = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.SEARCH, {
        params: {
          query: debouncedSearchTerm,
          page: currentPage - 1,
          size: patientsPerPage,
        },
      });
      setPatients(response.data.content);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      setError("Erreur lors du chargement des patients");
      setLoading(false);
      console.error("Error fetching patients:", err);
    }
  }, [currentPage, debouncedSearchTerm, patientsPerPage]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleViewPatient = (patientId: number) => {
    navigate(`/admin/dashboard/patient/view/${patientId}`);
  };

  const handleUpdatePatient = (patientId: number) => {
    navigate(`/admin/dashboard/account/patient/edit/${patientId}`);
  };

  const handleDeletePatient = async (patientId: number) => {
    const isConfirmed = await confirm("Êtes-vous sûr de vouloir supprimer ce patient ?", {
      title: "Supprimer le patient",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });

    if (!isConfirmed) return;
    try {
      await axiosInstance.delete(API_ENDPOINTS.PATIENTS.DELETE(patientId));
      fetchPatients(); // Refresh list
      toast.success("Patient supprimé avec succès");
      setError(null);
    } catch (err) {
      console.error("Error deleting patient:", err);
      toast.error("Erreur lors de la suppression du patient");
      setError("Erreur lors de la suppression du patient");
    }
  };

  const formatDate = (dateString: string | undefined) =>
    dateString ? new Date(dateString).toLocaleDateString("fr-FR") : "";

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  if (loading && patients.length === 0)
    return <LoadingScreen message="Chargement des patients..." role="admin" />;
  if (error) return <ResponseMessage message={error} role="admin" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Gestion des Patients"
        description="Visualisez et gérez les comptes des patients."
        action={{
          label: "Nouveau Patient",
          to: "/admin/dashboard/account/patient/new",
          icon: <Plus className="w-4 h-4" />,
        }}
        role="admin"
      />
      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-6xl mx-auto relative min-h-[400px]">
          {loading && patients.length > 0 && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-100 border-t-blue-600 mb-3"></div>
                <p className="text-sm text-blue-600 font-medium">
                  Actualisation...
                </p>
              </div>
            </div>
          )}
          <div className="relative flex-grow mb-6">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Rechercher par nom, téléphone ou email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {[
                    { label: "Nom", class: "md:w-1/6" },
                    { label: "Prénom", class: "md:w-1/6" },
                    {
                      label: "Date de naissance",
                      class: "hidden lg:table-cell md:w-1/12",
                    },
                    { label: "Genre", class: "hidden lg:table-cell md:w-1/12" },
                    { label: "Téléphone", class: "md:w-1/6" },
                    { label: "Email", class: "md:w-1/6" },
                    {
                      label: "Adresse",
                      class: "hidden lg:table-cell md:w-1/6",
                    },
                    { label: "Actions", class: "md:w-1/12" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {patients.length > 0 ? (
                  patients.map((patient) => (
                    <tr
                      key={patient.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 text-sm">
                        {patient.lastName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {patient.firstName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {formatDate(patient.dateOfBirth)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {patient.gender === "M"
                          ? "Masculin"
                          : patient.gender === "F"
                            ? "Féminin"
                            : "Autre"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {patient.phone}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-[150px]">
                        {patient.email}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm hidden lg:table-cell truncate max-w-[150px]">
                        {patient.address}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() => handleViewPatient(patient.id)}
                            aria-label="Voir le patient"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
                            onClick={() => handleUpdatePatient(patient.id)}
                            aria-label="Modifier le patient"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 transition-colors"
                            onClick={() => handleDeletePatient(patient.id)}
                            aria-label="Supprimer le patient"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-3 text-center text-gray-500 text-sm"
                    >
                      Aucun patient trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage de la page {currentPage} sur {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  totalPages <= 5
                    ? i + 1
                    : currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientAccount;
